import { BACKEND_URL } from "../config";
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const RequestInvite = () => {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [postCode, setPostCode] = useState('');
  const [country, setCountry] = useState('');
  const [howFound, setHowFound] = useState('');
  const [whyAccept, setWhyAccept] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [correctAnswer, setCorrectAnswer] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const API = `${BACKEND_URL}/api`;

  // Generate simple math captcha
  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCorrectAnswer(num1 + num2);
    setCaptchaQuestion(`What is ${num1} + ${num2}?`);
    setCaptchaAnswer('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!firstName || !email || !postCode || !country || !howFound || !whyAccept) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (parseInt(captchaAnswer) !== correctAnswer) {
      toast.error('Incorrect answer. Please try again.');
      generateCaptcha();
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/request-invite`, {
        first_name: firstName,
        email: email,
        post_code: postCode,
        country: country,
        how_found: howFound,
        why_accept: whyAccept
      });

      if (response.data.success) {
        toast.success('Invite request submitted successfully!');
        setSubmitted(true);
      } else {
        toast.error(response.data.message || 'Failed to submit request');
      }
    } catch (err) {
      console.error('Request invite error:', err);
      toast.error(err.response?.data?.message || 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{
          maxWidth: '500px',
          width: '100%',
          background: 'white',
          borderRadius: '16px',
          padding: '3rem',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '1.5rem' }}>✅</div>
          <h2 style={{ fontSize: '1.75rem', color: '#0c4a6e', marginBottom: '1rem' }}>
            Request Submitted!
          </h2>
          <p style={{ fontSize: '1rem', color: '#64748b', marginBottom: '2rem' }}>
            Thank you for your interest! We'll review your request and send an invite link to <strong>{email}</strong> soon.
          </p>
          <Link
            to="/"
            style={{
              display: 'inline-block',
              padding: '0.875rem 2rem',
              background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '0.875rem'
            }}
          >
            Back to Home Page
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem'
    }}>
      <div style={{
        maxWidth: '500px',
        width: '100%',
        background: 'white',
        borderRadius: '16px',
        padding: '2.5rem',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', color: '#0c4a6e', marginBottom: '0.5rem', fontWeight: '700' }}>
            📨 Request Invite
          </h1>
          <p style={{ fontSize: '0.875rem', color: '#64748b' }}>
            Enter your details to request access to Trade Blindfolded
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* First Name */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#334155',
              marginBottom: '0.5rem'
            }}>
              First Name
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Enter your first name"
              style={{
                width: '100%',
                padding: '0.875rem',
                background: 'rgba(255, 255, 255, 0.8)',
                border: '2px solid rgba(8, 145, 178, 0.3)',
                borderRadius: '8px',
                fontSize: '1rem',
                color: '#0f172a',
                outline: 'none',
                transition: 'all 0.3s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#0891b2';
                e.target.style.boxShadow = '0 0 0 3px rgba(8, 145, 178, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(8, 145, 178, 0.3)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Email */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#334155',
              marginBottom: '0.5rem'
            }}>
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              style={{
                width: '100%',
                padding: '0.875rem',
                background: 'rgba(255, 255, 255, 0.8)',
                border: '2px solid rgba(8, 145, 178, 0.3)',
                borderRadius: '8px',
                fontSize: '1rem',
                color: '#0f172a',
                outline: 'none',
                transition: 'all 0.3s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#0891b2';
                e.target.style.boxShadow = '0 0 0 3px rgba(8, 145, 178, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(8, 145, 178, 0.3)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Post Code */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#334155',
              marginBottom: '0.5rem'
            }}>
              Post Code
            </label>
            <input
              type="text"
              value={postCode}
              onChange={(e) => setPostCode(e.target.value)}
              placeholder="Enter your post code"
              style={{
                width: '100%',
                padding: '0.875rem',
                background: 'rgba(255, 255, 255, 0.8)',
                border: '2px solid rgba(8, 145, 178, 0.3)',
                borderRadius: '8px',
                fontSize: '1rem',
                color: '#0f172a',
                outline: 'none',
                transition: 'all 0.3s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#0891b2';
                e.target.style.boxShadow = '0 0 0 3px rgba(8, 145, 178, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(8, 145, 178, 0.3)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Country */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#334155',
              marginBottom: '0.5rem'
            }}>
              Country
            </label>
            <input
              type="text"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder="Enter your country"
              style={{
                width: '100%',
                padding: '0.875rem',
                background: 'rgba(255, 255, 255, 0.8)',
                border: '2px solid rgba(8, 145, 178, 0.3)',
                borderRadius: '8px',
                fontSize: '1rem',
                color: '#0f172a',
                outline: 'none',
                transition: 'all 0.3s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#0891b2';
                e.target.style.boxShadow = '0 0 0 3px rgba(8, 145, 178, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(8, 145, 178, 0.3)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* How Did You Find Out */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#334155',
              marginBottom: '0.5rem'
            }}>
              How did you find out about us?
            </label>
            <select
              value={howFound}
              onChange={(e) => setHowFound(e.target.value)}
              style={{
                width: '100%',
                padding: '0.875rem',
                background: 'rgba(255, 255, 255, 0.8)',
                border: '2px solid rgba(8, 145, 178, 0.3)',
                borderRadius: '8px',
                fontSize: '1rem',
                color: '#0f172a',
                outline: 'none',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#0891b2';
                e.target.style.boxShadow = '0 0 0 3px rgba(8, 145, 178, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(8, 145, 178, 0.3)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <option value="">-- Select an option --</option>
              <option value="Word of mouth">Word of mouth</option>
              <option value="Google Search">Google Search</option>
              <option value="Social Media">Social Media</option>
            </select>
          </div>

          {/* Why Should We Accept You */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#334155',
              marginBottom: '0.5rem'
            }}>
              Why do you want to trade blindfolded?
            </label>
            <textarea
              value={whyAccept}
              onChange={(e) => setWhyAccept(e.target.value)}
              placeholder="Share your reasons for wanting to trade blindfolded..."
              rows="4"
              style={{
                width: '100%',
                padding: '0.875rem',
                background: 'rgba(255, 255, 255, 0.8)',
                border: '2px solid rgba(8, 145, 178, 0.3)',
                borderRadius: '8px',
                fontSize: '1rem',
                color: '#0f172a',
                outline: 'none',
                transition: 'all 0.3s ease',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#0891b2';
                e.target.style.boxShadow = '0 0 0 3px rgba(8, 145, 178, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(8, 145, 178, 0.3)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Human Verification */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{
              padding: '1rem',
              background: 'rgba(8, 145, 178, 0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(8, 145, 178, 0.2)',
              marginBottom: '0.75rem'
            }}>
              <p style={{ fontSize: '0.875rem', color: '#334155', fontWeight: '600', marginBottom: '0.75rem' }}>
                🤖 Verify you're human
              </p>
              <p style={{ fontSize: '1rem', color: '#0c4a6e', fontWeight: '700', marginBottom: '0' }}>
                {captchaQuestion}
              </p>
            </div>
            <input
              type="number"
              value={captchaAnswer}
              onChange={(e) => setCaptchaAnswer(e.target.value)}
              placeholder="Enter your answer"
              style={{
                width: '100%',
                padding: '0.875rem',
                background: 'rgba(255, 255, 255, 0.8)',
                border: '2px solid rgba(8, 145, 178, 0.3)',
                borderRadius: '8px',
                fontSize: '1rem',
                color: '#0f172a',
                outline: 'none',
                transition: 'all 0.3s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#0891b2';
                e.target.style.boxShadow = '0 0 0 3px rgba(8, 145, 178, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(8, 145, 178, 0.3)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '1rem',
              background: loading ? '#94a3b8' : 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(8, 145, 178, 0.3)'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(8, 145, 178, 0.5)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(8, 145, 178, 0.3)';
              }
            }}
          >
            {loading ? 'Submitting...' : 'Submit Request'}
          </button>
        </form>

        {/* Back to Home Page Link */}
        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          <Link
            to="/"
            style={{
              fontSize: '0.875rem',
              color: '#0891b2',
              textDecoration: 'none',
              fontWeight: '600'
            }}
            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
          >
            ← Back to Home Page
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RequestInvite;
